
import { Vehicle, Employee } from '../../types';
import { createRepo, ListQuery } from '../repo';
import { DB_KEYS } from '../dbKeys';
import { normalizePlate, normalizeVin, emptyToNull } from '../../shared/validation/vehicle';
import { encrypt } from '../security/encryption';
import { cloudSync } from '../sync/CloudSyncService';

/**
 * Migration function for vehicles
 * Adds new fields and normalizes existing data
 */
function migrateVehicle(item: any): { item: Vehicle; changed: boolean } {
    let changed = false;

    // Add vehicleCategory if missing
    if (!item.vehicleCategory) {
        item.vehicleCategory = 'UNKNOWN';
        changed = true;
    }

    // Normalize plate number
    if (item.plateNumber) {
        const normalized = normalizePlate(item.plateNumber);
        if (normalized !== item.plateNumber) {
            item.plateNumber = normalized;
            changed = true;
        }
    }

    // Normalize VIN
    const normalizedVin = emptyToNull(normalizeVin(item.vin));
    if (normalizedVin !== item.vin) {
        item.vin = normalizedVin;
        changed = true;
    }

    // Normalize body number
    const normalizedBody = emptyToNull(item.bodyNumber);
    if (normalizedBody !== item.bodyNumber) {
        item.bodyNumber = normalizedBody;
        changed = true;
    }

    // Normalize chassis number
    const normalizedChassis = emptyToNull(item.chassisNumber);
    if (normalizedChassis !== item.chassisNumber) {
        item.chassisNumber = normalizedChassis;
        changed = true;
    }

    return { item: item as Vehicle, changed };
}

/**
 * Normalization function applied on write operations
 */
function normalizeVehicleOnWrite(v: Vehicle): Vehicle {
    return {
        ...v,
        plateNumber: normalizePlate(v.plateNumber),
        vin: emptyToNull(normalizeVin(v.vin)),
        bodyNumber: emptyToNull(v.bodyNumber),
        chassisNumber: emptyToNull(v.chassisNumber),
        // Don't set default category, let it be null
    };
}

import { employeeRepo } from './employees';
import { decrypt } from '../security/encryption';

const baseVehicleRepo = createRepo<Vehicle>(DB_KEYS.VEHICLES, {
    migrate: migrateVehicle,
    normalizeOnWrite: normalizeVehicleOnWrite,
    cloudCollection: 'vehicles',
    beforeCloudSync: async (item) => {
        const security = await cloudSync.getEncryptionParams();

        // Resolve assignedDriverId to firebaseUid for cloud security rules
        let assignedDriverUids: string[] = [];
        if (item.assignedDriverId) {
            const driver = await employeeRepo.getById(item.assignedDriverId);
            if (driver?.firebaseUid) {
                assignedDriverUids = [driver.firebaseUid];
            }
        }

        const encryptField = async (val: string | undefined): Promise<string | null> => {
            if (!val || val.trim() === '') return null;
            return security ? await encrypt(val, security.key || security.password, security.salt) : val;
        };

        const plateNumber = await encryptField(item.plateNumber) || item.plateNumber;
        const vin = await encryptField(item.vin);
        const chassisNumber = await encryptField(item.chassisNumber);
        const bodyNumber = await encryptField(item.bodyNumber);

        // Encrypt Objects
        let stsEncrypted = null;
        const stsObj = item.sts || (item.stsSeries ? {
            series: item.stsSeries,
            number: item.stsNumber,
            issueDate: item.stsIssueDate
        } : null);
        if (stsObj) stsEncrypted = await encryptField(JSON.stringify(stsObj));

        let ptsEncrypted = null;
        const ptsObj = item.pts || (item.ptsSeries || item.eptsNumber ? {
            type: item.ptsType,
            series: item.ptsSeries,
            number: item.ptsNumber,
            eptsNumber: item.eptsNumber
        } : null);
        if (ptsObj) ptsEncrypted = await encryptField(JSON.stringify(ptsObj));

        let consumptionEncrypted = null;
        if (item.fuelConsumptionRates) {
            consumptionEncrypted = await encryptField(JSON.stringify(item.fuelConsumptionRates));
        }

        // Regulatory Docs
        const osagoSeries = await encryptField(item.osagoSeries);
        const osagoNumber = await encryptField(item.osagoNumber);
        const osagoStartDate = await encryptField(item.osagoStartDate);
        const osagoEndDate = await encryptField(item.osagoEndDate);

        const diagnosticCardNumber = await encryptField(item.diagnosticCardNumber);
        const diagnosticCardIssueDate = await encryptField(item.diagnosticCardIssueDate);
        const diagnosticCardExpiryDate = await encryptField(item.diagnosticCardExpiryDate);

        // Inspections
        const svtoSeries = await encryptField(item.svtoSeries);
        const svtoNumber = await encryptField(item.svtoNumber);
        const svtoIssueDate = await encryptField(item.svtoIssueDate);
        const svtoExpiryDate = await encryptField(item.svtoExpiryDate);
        const akttoNumber = await encryptField(item.akttoNumber);
        const akttoDate = await encryptField(item.akttoDate);
        const akttoExpiryDate = await encryptField(item.akttoExpiryDate);

        return {
            id: item.id,
            plateNumber: plateNumber ?? null,
            mileage: item.mileage ?? 0,
            brand: item.brand ?? null,
            year: item.year ?? null,
            organizationId: item.organizationId ?? null,
            status: item.status ?? 'Active',
            vehicleType: item.vehicleType ?? null,
            vehicleCategory: item.vehicleCategory ?? null,
            assignedDriverId: item.assignedDriverId ?? null,
            assignedDriverUids: assignedDriverUids ?? [],
            fuelTypeId: item.fuelTypeId ?? null,
            fuelTankCapacity: item.fuelTankCapacity ?? null,
            // --- Encrypted Full Backup Fields ---
            vin: vin ?? null,
            chassisNumber: chassisNumber ?? null,
            bodyNumber: bodyNumber ?? null,
            sts: stsEncrypted ?? null,
            pts: ptsEncrypted ?? null,
            fuelConsumptionRates: consumptionEncrypted ?? null,
            osagoSeries: osagoSeries ?? null,
            osagoNumber: osagoNumber ?? null,
            osagoStartDate: osagoStartDate ?? null,
            osagoEndDate: osagoEndDate ?? null,
            diagnosticCardNumber: diagnosticCardNumber ?? null,
            diagnosticCardIssueDate: diagnosticCardIssueDate ?? null,
            diagnosticCardExpiryDate: diagnosticCardExpiryDate ?? null,
            svtoSeries: svtoSeries ?? null,
            svtoNumber: svtoNumber ?? null,
            svtoIssueDate: svtoIssueDate ?? null,
            svtoExpiryDate: svtoExpiryDate ?? null,
            akttoNumber: akttoNumber ?? null,
            akttoDate: akttoDate ?? null,
            akttoExpiryDate: akttoExpiryDate ?? null,
            // ------------------------------------
            currentFuel: item.currentFuel ?? 0,
            inspectionDocType: item.inspectionDocType ?? null,
            maintenanceIntervalKm: item.maintenanceIntervalKm ?? null,
            lastMaintenanceMileage: item.lastMaintenanceMileage ?? null,
            engineHours: item.engineHours ?? null,
            updatedAt: new Date().toISOString()
        };
    }
});

export const vehicleRepo = {
    ...baseVehicleRepo,
    async upsertFromCloud(items: any[]) {
        if (items.length === 0) return;
        const security = await cloudSync.getEncryptionParams();

        const processedItems = await Promise.all(items.map(async (item) => {
            // 1. Decrypt plateNumber
            if (security && item.plateNumber && typeof item.plateNumber === 'string' && item.plateNumber.includes(':')) {
                try {
                    const decrypted = await decrypt(item.plateNumber, security.key || security.password, security.salt);
                    if (decrypted) item.plateNumber = decrypted;
                } catch (e) { }
            }

            // 2. Decrypt Other Sensitive Fields
            const sensitiveFields = [
                'vin', 'chassisNumber', 'bodyNumber',
                'osagoSeries', 'osagoNumber', 'osagoStartDate', 'osagoEndDate',
                'diagnosticCardNumber', 'diagnosticCardIssueDate', 'diagnosticCardExpiryDate',
                'svtoSeries', 'svtoNumber', 'svtoIssueDate', 'svtoExpiryDate',
                'akttoNumber', 'akttoDate', 'akttoExpiryDate'
            ];
            for (const field of sensitiveFields) {
                if (security && item[field] && typeof item[field] === 'string' && item[field].includes(':')) {
                    try {
                        const decrypted = await decrypt(item[field], security.key || security.password, security.salt);
                        if (decrypted) item[field] = decrypted;
                    } catch (e) { }
                }
            }

            // 3. Decrypt Objects: STS, PTS, consumption (JSON -> String)
            const objectFields = ['sts', 'pts', 'fuelConsumptionRates'];
            for (const field of objectFields) {
                if (security && item[field] && typeof item[field] === 'string' && item[field].startsWith('{') === false) {
                    try {
                        const decryptedJson = await decrypt(item[field], security.key || security.password, security.salt);
                        if (decryptedJson) {
                            const obj = JSON.parse(decryptedJson);
                            item[field] = obj;

                            // Restore flattened fields for legacy compatibility
                            if (field === 'sts') {
                                item.stsSeries = obj.series;
                                item.stsNumber = obj.number;
                                item.stsIssueDate = obj.issueDate;
                            } else if (field === 'pts') {
                                item.ptsType = obj.type;
                                item.ptsSeries = obj.series;
                                item.ptsNumber = obj.number;
                                item.eptsNumber = obj.eptsNumber;
                            }
                        }
                    } catch (e) { }
                }
            }

            return {
                ...item,
                status: item.status || 'Active'
            } as Vehicle;
        }));

        await baseVehicleRepo.updateBulk(processedItems, true);
    }
};

export const getVehicles = async () => (await vehicleRepo.list({ pageSize: 1000 })).data;
export const fetchVehicles = async (q: ListQuery) => vehicleRepo.list(q);
export const addVehicle = (item: Omit<Vehicle, 'id'>) => vehicleRepo.create(item);
export const updateVehicle = (item: Vehicle) => vehicleRepo.update(item.id, item);
export const deleteVehicle = (id: string) => vehicleRepo.remove(id);

export const getIssues = async (params: { vehicleId?: string }) => {
    const vehicles = (await vehicleRepo.list({ pageSize: 1000 })).data;
    const employees = (await employeeRepo.list({ pageSize: 1000 })).data;

    const targets = params.vehicleId ? vehicles.filter(v => v.id === params.vehicleId) : vehicles;

    // Расширенный тип с флагом isExpired и source для группировки
    const expiringDocs: Array<{
        type: string;
        name: string;
        date: string;
        isExpired: boolean;  // true = истёк, false = истекает
        source: 'vehicle' | 'driver';
        sourceId: string;
        sourceName: string;
    }> = [];

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const warningThreshold = new Date();
    warningThreshold.setDate(now.getDate() + 30); // Предупреждать за 30 дней

    // 1. Проверка ТС
    for (const v of targets) {
        if (v.status !== 'Active') continue;

        const vehicleSourceName = `${v.plateNumber} (${v.brand})`;

        // Check ОСАГО
        if (v.osagoEndDate) {
            const date = new Date(v.osagoEndDate);
            if (date <= warningThreshold) {
                expiringDocs.push({
                    type: 'ОСАГО',
                    name: vehicleSourceName,
                    date: v.osagoEndDate,
                    isExpired: date < now,
                    source: 'vehicle',
                    sourceId: v.id,
                    sourceName: vehicleSourceName
                });
            }
        }

        // Check Диагностическая карта (для обычных ТС)
        if (v.vehicleType !== 'Спецтехника' && v.diagnosticCardExpiryDate) {
            const date = new Date(v.diagnosticCardExpiryDate);
            if (date <= warningThreshold) {
                expiringDocs.push({
                    type: 'Диагностическая карта',
                    name: vehicleSourceName,
                    date: v.diagnosticCardExpiryDate,
                    isExpired: date < now,
                    source: 'vehicle',
                    sourceId: v.id,
                    sourceName: vehicleSourceName
                });
            }
        }

        // Check Св-во ТО (для спецтехники)
        if (v.vehicleType === 'Спецтехника' && v.inspectionDocType === 'SVTO' && v.svtoExpiryDate) {
            const date = new Date(v.svtoExpiryDate);
            if (date <= warningThreshold) {
                expiringDocs.push({
                    type: 'Св-во ТО',
                    name: vehicleSourceName,
                    date: v.svtoExpiryDate,
                    isExpired: date < now,
                    source: 'vehicle',
                    sourceId: v.id,
                    sourceName: vehicleSourceName
                });
            }
        }

        // Check Maintenance (ТО по пробегу или моточасам)
        if (v.maintenanceIntervalKm && v.lastMaintenanceMileage !== undefined && v.lastMaintenanceMileage !== null) {
            // Для спецтехники — проверяем моточасы (порог 50 часов)
            if (v.vehicleType === 'Спецтехника') {
                const currentHours = v.engineHours ?? 0;
                const nextService = v.lastMaintenanceMileage + v.maintenanceIntervalKm;
                const remaining = nextService - currentHours;
                if (remaining <= 50) {  // Порог 50 часов
                    expiringDocs.push({
                        type: 'Тех. обслуживание',
                        name: `${vehicleSourceName}: ${remaining < 0 ? `Просрочено на ${Math.abs(Math.round(remaining))} ч` : `Осталось ${Math.round(remaining)} ч`}`,
                        date: new Date().toISOString(),
                        isExpired: remaining < 0,
                        source: 'vehicle',
                        sourceId: v.id,
                        sourceName: vehicleSourceName
                    });
                }
            } else {
                // Для обычных ТС — проверяем пробег (порог 1000 км)
                const nextService = v.lastMaintenanceMileage + v.maintenanceIntervalKm;
                const remaining = nextService - v.mileage;
                if (remaining <= 1000) {
                    expiringDocs.push({
                        type: 'Тех. обслуживание',
                        name: `${vehicleSourceName}: ${remaining < 0 ? `Просрочено на ${Math.abs(remaining)} км` : `Осталось ${remaining} км`}`,
                        date: new Date().toISOString(),
                        isExpired: remaining < 0,
                        source: 'vehicle',
                        sourceId: v.id,
                        sourceName: vehicleSourceName
                    });
                }
            }
        }
    }

    // 2. Проверка водителей (только ВУ, медсправка отключена)
    let targetDrivers = employees.filter(e => e.status === 'Active' && e.employeeType === 'driver');

    // Если выбран конкретный автомобиль, проверяем только закрепленного водителя
    if (params.vehicleId) {
        const vehicle = vehicles.find(v => v.id === params.vehicleId);
        if (vehicle && vehicle.assignedDriverId) {
            targetDrivers = targetDrivers.filter(d => d.id === vehicle.assignedDriverId);
        } else {
            // Если у машины нет закрепленного водителя, водителей не проверяем
            targetDrivers = [];
        }
    }

    for (const d of targetDrivers) {
        // Водительское удостоверение
        if (d.documentExpiry) {
            const date = new Date(d.documentExpiry);
            if (date <= warningThreshold) {
                expiringDocs.push({
                    type: 'Водительское удостоверение',
                    name: d.shortName,
                    date: d.documentExpiry,
                    isExpired: date < now,
                    source: 'driver',
                    sourceId: d.id,
                    sourceName: d.shortName
                });
            }
        }

        // Медицинская справка — ОТКЛЮЧЕНО по запросу
        // if (d.medicalCertificateExpiryDate) {
        //     const date = new Date(d.medicalCertificateExpiryDate);
        //     if (date <= warningThreshold) {
        //         expiringDocs.push({
        //             type: 'Медицинская справка',
        //             name: d.shortName,
        //             date: d.medicalCertificateExpiryDate,
        //             isExpired: date < now,
        //             source: 'driver',
        //             sourceId: d.id,
        //             sourceName: d.shortName
        //         });
        //     }
        // }
    }

    return { expiringDocs };
};
